/**
 * CitationPopover — floating list of source citations for the
 * writer's currently selected document text. Spec 26 C6.
 *
 * Sits above the CommandRail input row, anchored via the rail's
 * relative-positioned root. Renders nothing when there are no
 * citations + no loading + no error (parent gates the mount on
 * having a meaningful selection).
 *
 * SRP: pure presentation. The selection→fetch driver is
 * useSelectionCitations; this component just renders.
 *
 * # DECISION: clicking a citation row is a no-op in v1 (cursor
 * #   is read-only display).
 * # WHY: existing Asthra source-jump infra requires more wiring
 * #      than the rail alone provides. The popover ships as a
 * #      reading-aid first; click-to-jump can be added without
 * #      changing the surface.
 * # DATE: 2026-04-22
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useState } from "react";
import type { Citation, CitationDerivation, CitationResponse } from "../../api/client";
import { splitPipeSegments } from "../../services/partialTables";
import AssistantMarkdown from "./AssistantMarkdown";
import DerivationTrace from "./DerivationTrace";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

export interface CitationPopoverProps {
  citations: ReadonlyArray<Citation>;
  isLoading: boolean;
  error: string | null;
  /** Server-side status from the last citation lookup. Drives the
   *  empty-state copy: "no_provenance" / "upstream_error" / "ok with
   *  zero results" each show a different message instead of the
   *  generic "no citations matched". Null until the first fetch. */
  status?: CitationResponse["status"] | null;
  /** Optional server-side human-readable message. When set and
   *  citations are empty, this is rendered verbatim in the empty
   *  state — keeps the UI honest with what the backend actually
   *  reported (e.g. "Could not determine which module generated
   *  this section."). */
  message?: string | null;
  /** Transaction IDs the backend looked up against Asthra. When
   *  provided AND the result was an empty "ok", surfaces a hint
   *  ("Looked up N source transactions, no matches.") so the writer
   *  can tell "no sources" apart from "sources but no overlap with
   *  selection". Optional. */
  transactionIdsUsed?: ReadonlyArray<string> | null;
  /** The text the citations were fetched for — header preview. */
  fetchedText: string;
  /** Click handler for the popover's × close button. The driver
   *  doesn't auto-clear on dismiss; parent owns the visibility
   *  decision (typically: re-show on next selection change). */
  onDismiss: () => void;
  /** "How this was calculated" for computed numbers in the selection —
   *  rendered above the citation list. Optional; empty when the selection
   *  holds no computed number. */
  derivations?: ReadonlyArray<CitationDerivation>;
  /** Optional retry handler. When provided AND ``error`` is set,
   *  the error state surfaces a Retry button next to the diagnostic
   *  text. Wired to the parent's force-fetch path (typically
   *  ``useSelectionCitations.fetchNow``). When omitted, the error
   *  state shows the diagnostic text alone (no affordance). */
  onRetry?: () => void;
}

const useStyles = makeStyles({
  root: {
    position: "absolute",
    bottom: "100%",
    // Center at 95% of the add-in width. A <dialog>'s UA default
    // `width: fit-content` ignores a `right` offset once a width is in
    // play, so wide citation rows used to overflow the right edge —
    // pin an explicit width + box-sizing instead of left/right insets.
    left: "50%",
    right: "auto",
    width: "95%",
    maxWidth: "95%",
    transform: "translateX(-50%)",
    boxSizing: "border-box",
    zIndex: 4,
    background: "#fff",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "4px",
    boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.06)",
    fontFamily: fonts.body,
    marginBottom: "4px",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 10px",
    background: surfaceColors.paperDim,
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  headerLabel: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: inkColors.ink4,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  headerPreview: {
    flex: 1,
    fontSize: "10.5px",
    color: inkColors.ink3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontStyle: "italic",
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: inkColors.ink4,
    cursor: "pointer",
    fontSize: "12px",
    padding: "0 4px",
    lineHeight: 1,
    "&:hover": { color: inkColors.ink2 },
  },
  list: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "240px",
    overflow: "auto",
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  row: {
    display: "flex",
    flexDirection: "column",
    borderBottom: `1px solid ${surfaceColors.line}`,
    "&:last-child": { borderBottomColor: "transparent" },
  },
  rowHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    padding: "6px 10px",
    fontSize: "11px",
    color: inkColors.ink2,
    background: "none",
    border: "none",
    width: "100%",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": { background: surfaceColors.paperDim },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
  chevron: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    minWidth: "10px",
    transition: "transform 120ms",
    userSelect: "none",
  },
  chevronExpanded: {
    transform: "rotate(90deg)",
  },
  citationNumber: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: brandColors.primary,
    minWidth: "20px",
  },
  rowTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowTitleExpanded: {
    overflow: "visible",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  rowTitleMeta: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 400,
    color: inkColors.ink4,
    marginLeft: "4px",
  },
  expandedPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "4px 10px 8px 38px",
    maxHeight: "160px",
    overflowY: "auto",
    borderTop: `1px solid ${surfaceColors.line}`,
    background: surfaceColors.paperDim,
  },
  excerpt: {
    fontSize: "10.5px",
    color: inkColors.ink3,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  rowTxnId: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    marginTop: "4px",
    wordBreak: "break-all",
  },
  tableWrap: {
    overflowX: "auto",
    maxWidth: "100%",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "10px",
  },
  tableHeaderCell: {
    textAlign: "left",
    padding: "3px 6px",
    fontWeight: 700,
    fontSize: "9.5px",
    color: inkColors.ink2,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    whiteSpace: "nowrap",
  },
  tableCell: {
    padding: "3px 6px",
    color: inkColors.ink3,
    border: `1px solid ${surfaceColors.line}`,
    verticalAlign: "top",
  },
  tableGroupHeaderCell: {
    padding: "3px 6px",
    fontWeight: 700,
    color: inkColors.ink2,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
  },
  state: {
    padding: "10px 12px",
    fontSize: "10.5px",
    color: inkColors.ink4,
    fontStyle: "italic",
  },
  errorRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
  },
  errorText: {
    flex: 1,
    fontSize: "10.5px",
    color: inkColors.ink4,
    fontStyle: "italic",
  },
  retryBtn: {
    flexShrink: 0,
    fontSize: "10.5px",
    fontWeight: 500,
    fontFamily: fonts.body,
    padding: "4px 10px",
    borderRadius: "999px",
    border: `1px solid ${brandColors.primary}`,
    background: brandColors.light,
    color: brandColors.primary,
    cursor: "pointer",
    "&:hover:not(:disabled)": {
      background: brandColors.primary,
      color: "#FFF",
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
});

/** Hard cap on rows rendered. Anything beyond is dropped silently —
 *  the popover prioritises a compact, scannable list over completeness.
 *  When the writer needs more, they can re-fetch with a tighter
 *  selection. */
const MAX_VISIBLE_ROWS = 1;

/** Pure formatter — exported for tests. Truncates the writer's
 *  selection text for the header preview. */
export function formatHeaderPreview(text: string, max = 40): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return `"${trimmed}"`;
  return `"${trimmed.slice(0, max - 1)}…"`;
}

/** Pure copy resolver — exported for tests. Picks the empty-state
 *  message in priority order:
 *
 *    1. server-supplied ``message`` (most specific — explains exactly
 *       why the lookup yielded zero rows: no_provenance, upstream
 *       error, or "no matches" with txn count),
 *    2. transaction-count hint when we know how many source txns
 *       were searched (turns "nothing matched" into something
 *       actionable),
 *    3. generic fallback.
 */
export function emptyStateCopy(
  status: CitationResponse["status"] | null | undefined,
  message: string | null | undefined,
  transactionIdsUsed: ReadonlyArray<string> | null | undefined,
): string {
  if (message?.trim()) return message;
  const txnCount = transactionIdsUsed?.length ?? 0;
  if (status === "ok" && txnCount > 0) {
    return `Looked up ${txnCount} source transaction${txnCount === 1 ? "" : "s"}, no matches for this selection.`;
  }
  return "No citations matched this selection.";
}

export interface ParsedCitationTableRow {
  cells: string[];
  /** True when every value column (all but the first) is empty — the
   *  backend's flattened format uses this to mark section/group
   *  headers (e.g. "Age||"), which render as a full-width label row
   *  instead of a normal data row. */
  isGroupHeader: boolean;
}

export interface ParsedCitationTable {
  headers: string[];
  rows: ParsedCitationTableRow[];
}

/** One piece of a citation excerpt: either a run of prose or a
 *  reconstructed table. A single ``context`` string can contain any
 *  number of these in any order (text → table → text → table → …),
 *  because the backend snippet is raw source text that may embed one
 *  or more tables between paragraphs. */
export type CitationSegment = { kind: "text"; text: string } | { kind: "table"; table: ParsedCitationTable };

const SEPARATOR_CELL_RE = /^:?-{2,}:?$/;
const SEPARATOR_ROW_RE = /\|\s*:?-{2,}:?\s*(?:\|\s*:?-{2,}:?\s*)+\|/;

/** A GFM alignment/separator row — every cell is dashes with optional
 *  alignment colons (`|---:|:---:|`). */
function isSeparatorCells(cells: string[]): boolean {
  return cells.length > 0 && cells.every((cell) => SEPARATOR_CELL_RE.test(cell));
}

/** Build a table model from a header row + data rows, padding ragged
 *  rows to a common width. Real backend dumps are often ragged (the
 *  separator row can have fewer cells than the data rows), so column
 *  count is the max seen rather than the header's or separator's. */
function buildTable(headerCells: string[], dataRows: string[][]): ParsedCitationTable | null {
  if (dataRows.length === 0) return null;
  const colCount = Math.max(headerCells.length, ...dataRows.map((r) => r.length));
  const pad = (cells: string[]): string[] =>
    cells.length >= colCount ? cells : cells.concat(Array(colCount - cells.length).fill(""));
  return {
    headers: pad(headerCells),
    rows: dataRows.map((cells) => {
      const padded = pad(cells);
      return { cells: padded, isGroupHeader: padded.slice(1).every((cell) => cell === "") };
    }),
  };
}

/** Last resort — the fully-flattened shape where an entire GFM table
 *  (rows and all) is collapsed onto a single line with no line breaks.
 *  Line-based detection can't see row boundaries here, so the
 *  separator's column count is used to walk the string in fixed-width
 *  `|cell|cell|…|` chunks. Returns the table plus any prose before/
 *  after it, with stray truncated-row pipe fragments trimmed. */
function parseFlattenedGfmTable(
  text: string,
): { table: ParsedCitationTable; leadingText: string; trailingText: string } | null {
  const sepMatch = SEPARATOR_ROW_RE.exec(text);
  if (!sepMatch) return null;

  const colCount = sepMatch[0].split("|").slice(1, -1).length;
  if (colCount < 2) return null;

  const rowRe = new RegExp(`\\|${"([^|]*)\\|".repeat(colCount)}`, "g");
  const rows: { cells: string[]; start: number; end: number }[] = [];
  let match: RegExpExecArray | null = rowRe.exec(text);
  while (match !== null) {
    rows.push({
      cells: match.slice(1).map((cell) => cell.trim()),
      start: match.index,
      end: match.index + match[0].length,
    });
    match = rowRe.exec(text);
  }

  const sepIndex = rows.findIndex((row) => isSeparatorCells(row.cells));
  if (sepIndex < 1) return null;

  const headerRow = rows[sepIndex - 1];
  const dataRows = rows.slice(sepIndex + 1);
  const table = buildTable(
    headerRow.cells,
    dataRows.map(({ cells }) => cells),
  );
  if (!table) return null;

  return {
    table,
    leadingText: text
      .slice(0, headerRow.start)
      .trim()
      .replace(/\|+\s*$/, "")
      .trim(),
    trailingText: text
      .slice(dataRows[dataRows.length - 1].end)
      .trim()
      .replace(/^\|+\s*/, "")
      .trim(),
  };
}

/** Row starts in a flattened pandas `to_markdown()` dump: every row opens
 *  with its integer index cell, and adjacent rows meet as `| |`. */
const INDEXED_ROW_START_RE = /(?:^|\|)\s*\|\s*(\d+)\s*\|/g;
/** Fewer consecutive indices than this is too weak a signal to call it a table. */
const MIN_INDEXED_ROWS = 3;

/** Last resort for flattened tables with no `|---|` separator — the shape
 *  a citation chunk takes when it was cut from the middle of a parsed
 *  table (`…cell | | 5 | Filter description | 47 mm … | | 6 | …`). Row
 *  boundaries are found from the pandas index column counting up by one;
 *  that index is dropped from the rendered table. A row cut at either end
 *  of the excerpt is kept and padded with `…`, as splitPipeSegments does. */
function parseIndexedFlatTable(
  text: string,
): { table: ParsedCitationTable; leadingText: string; trailingText: string } | null {
  const starts = [...text.matchAll(INDEXED_ROW_START_RE)];

  // Longest run of row starts whose index increases by exactly one.
  let best: RegExpMatchArray[] = [];
  let run: RegExpMatchArray[] = [];
  for (const m of starts) {
    const prev = run[run.length - 1];
    run = prev && Number(m[1]) === Number(prev[1]) + 1 && (m.index ?? 0) > (prev.index ?? 0) ? [...run, m] : [m];
    if (run.length > best.length) best = run;
  }
  if (best.length < MIN_INDEXED_ROWS) return null;

  const cellsOf = (s: string): string[] => s.split("|").map((cell) => cell.trim());
  const end = (m: RegExpMatchArray): number => (m.index ?? 0) + m[0].length;
  const complete = best.slice(0, -1).map((m, k) => cellsOf(text.slice(end(m), best[k + 1].index)));
  const colCount = Math.max(...complete.map((cells) => cells.length));

  // Last row: take its cells, and anything past them is prose after the table.
  const last = best[best.length - 1];
  const tailParts = text.slice(end(last)).split("|");
  let lastRow: string[];
  let trailingText = "";
  if (tailParts.length > colCount) {
    lastRow = tailParts.slice(0, colCount).map((cell) => cell.trim());
    trailingText = tailParts
      .slice(colCount)
      .join("|")
      .trim()
      .replace(/^\|+\s*/, "")
      .trim();
  } else {
    lastRow = cellsOf(tailParts.join("|")).filter((cell, j, all) => cell !== "" || j < all.length - 1);
    if (lastRow.length < colCount) lastRow = [...lastRow, "…", ...Array(colCount - lastRow.length - 1).fill("")];
  }

  // Before the first row start: a row cut on its left, possibly after prose.
  const first = best[0];
  const leadParts = text.slice(0, first.index).split("|");
  let leadingText = "";
  const rows = [...complete, lastRow];
  if (leadParts.length > 1 || ((first.index ?? 0) > 0 && text[first.index ?? 0] === "|")) {
    const cut = leadParts.slice(-colCount).map((cell) => cell.trim());
    leadingText = leadParts
      .slice(0, -colCount)
      .join("|")
      .replace(/\|\s*\d+\s*$/, "")
      .trim();
    const missing = colCount - cut.length;
    rows.unshift(missing > 0 ? ["…", ...Array(missing - 1).fill(""), ...cut] : cut);
  } else {
    leadingText = leadParts.join("|").trim();
  }

  const table = buildTable(Array(colCount).fill(""), rows);
  return table ? { table, leadingText, trailingText } : null;
}

/** Pure parser — exported for tests. Splits a raw citation excerpt
 *  into ordered text/table segments so mixed content (prose with one
 *  or more embedded tables) renders correctly instead of collapsing
 *  to plain text. Detection is layered from most to least structured:
 *
 *    1. line-based pipe tables via splitPipeSegments (partialTables.ts):
 *       with or without a `|---|` separator, and tolerant of excerpts
 *       cut mid-table — a missing header, a cut first or last row,
 *    2. the fully-flattened single-line shape, as a whole-string
 *       fallback when nothing line-based was found,
 *    3. a flattened excerpt cut mid-table (no separator), with rows
 *       found from the pandas index column.
 *
 *  Content with no table at all comes back as a single text segment,
 *  which callers render exactly as before. */
export function parseCitationSegments(text: string): CitationSegment[] {
  const lineSegments: CitationSegment[] = [];
  for (const seg of splitPipeSegments(text)) {
    if (seg.kind === "table") {
      const table = buildTable(seg.table.headers, seg.table.rows);
      if (table) lineSegments.push({ kind: "table", table });
      continue;
    }
    const trimmed = seg.text.trim();
    if (trimmed) lineSegments.push({ kind: "text", text: trimmed });
  }
  if (lineSegments.some((seg) => seg.kind === "table")) return lineSegments;

  const flat = parseFlattenedGfmTable(text) ?? parseIndexedFlatTable(text);
  if (flat) {
    const result: CitationSegment[] = [];
    if (flat.leadingText) result.push({ kind: "text", text: flat.leadingText });
    result.push({ kind: "table", table: flat.table });
    if (flat.trailingText) result.push({ kind: "text", text: flat.trailingText });
    return result;
  }

  const trimmed = text.trim();
  return trimmed ? [{ kind: "text", text: trimmed }] : [];
}

const CitationTable: React.FC<{
  table: ParsedCitationTable;
  styles: ReturnType<typeof useStyles>;
}> = ({ table, styles }) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      {/* A table cut above its header row comes back with blank headers. */}
      {table.headers.some((header) => header !== "") && (
        <thead>
          <tr>
            {table.headers.map((header, j) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: header text can repeat or be blank
              <th key={j} className={styles.tableHeaderCell}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {table.rows.map((row) =>
          row.isGroupHeader ? (
            <tr key={`group-${row.cells[0]}`}>
              <td className={styles.tableGroupHeaderCell} colSpan={table.headers.length}>
                {row.cells[0]}
              </td>
            </tr>
          ) : (
            <tr key={row.cells.join("␟")}>
              {row.cells.map((cell, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                <td key={j} className={styles.tableCell}>
                  {cell}
                </td>
              ))}
            </tr>
          ),
        )}
      </tbody>
    </table>
  </div>
);

export const CitationPopover: React.FC<CitationPopoverProps> = ({
  citations,
  isLoading,
  error,
  status,
  message,
  transactionIdsUsed,
  derivations = [],
  fetchedText,
  onDismiss,
  onRetry,
}) => {
  const styles = useStyles();
  // Hard cap — anything beyond the top N is intentionally dropped so
  // the popover stays compact above the chat input row.
  const visible = citations.slice(0, MAX_VISIBLE_ROWS);
  const headingPreview = formatHeaderPreview(fetchedText);
  // Expanded rows tracked by the same key the row uses for React.
  // Reset whenever the visible set changes (new fetch, new selection).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const visibleKeysSig = visible
    .map((c, i) => c.transaction_id || `${c.document_name}-${c.citation_number}-${i}`)
    .join("|");
  React.useEffect(() => {
    if (visibleKeysSig !== "") setExpanded(new Set());
    else setExpanded(new Set());
  }, [visibleKeysSig]);
  const toggleRow = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <dialog className={styles.root} aria-label="Citations for selected text" open>
      <div className={styles.header}>
        <span className={styles.headerLabel}>Citations · {citations.length}</span>
        <span className={styles.headerPreview}>{headingPreview}</span>
        <button type="button" className={styles.closeBtn} onClick={onDismiss} aria-label="Dismiss citation popover">
          ×
        </button>
      </div>
      {isLoading && <div className={styles.state}>Looking up citations…</div>}
      {error && !isLoading && (
        <div className={styles.errorRow}>
          <span className={styles.errorText}>Couldn't load citations ({error}).</span>
          {onRetry && (
            <button type="button" className={styles.retryBtn} onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      )}
      {!isLoading && derivations.map((d) => <DerivationTrace key={d.transaction_id || d.operation} derivation={d} />)}
      {!isLoading && !error && citations.length === 0 && derivations.length === 0 && (
        <div className={styles.state}>{emptyStateCopy(status, message, transactionIdsUsed)}</div>
      )}
      {!isLoading && !error && visible.length > 0 && (
        <ul className={styles.list}>
          {visible.map((c, i) => {
            const key = c.transaction_id || `${c.document_name}-${c.citation_number}-${i}`;
            const isExpanded = expanded.has(key);
            const hasSection = c.section_name && c.section_name.trim().length > 0;
            const hasPage = c.page_no > 0;
            const contextSegments = c.context ? parseCitationSegments(c.context) : [];
            const hasTable = contextSegments.some((seg) => seg.kind === "table");
            return (
              <li key={key} className={styles.row}>
                <button
                  type="button"
                  className={styles.rowHeader}
                  onClick={() => toggleRow(key)}
                  aria-expanded={isExpanded}
                  aria-label={`Citation ${c.citation_number}: ${c.document_name}`}
                >
                  <span
                    className={mergeClasses(styles.chevron, isExpanded && styles.chevronExpanded)}
                    aria-hidden="true"
                  >
                    ▸
                  </span>
                  <span className={styles.citationNumber}>[{c.citation_number}]</span>
                  <span className={mergeClasses(styles.rowTitle, isExpanded && styles.rowTitleExpanded)}>
                    {c.document_name}
                    {(hasSection || hasPage) && (
                      <span className={styles.rowTitleMeta}>
                        {hasSection ? ` · ${c.section_name}` : ""}
                        {hasPage ? ` · p.${c.page_no}` : ""}
                      </span>
                    )}
                  </span>
                </button>
                {isExpanded && (c.context || c.transaction_id) && (
                  <div className={styles.expandedPanel}>
                    {c.context &&
                      (hasTable ? (
                        contextSegments.map((seg) =>
                          seg.kind === "table" ? (
                            <CitationTable
                              key={`table-${seg.table.headers.join("|")}-${seg.table.rows[0]?.cells.join("|") ?? ""}`}
                              table={seg.table}
                              styles={styles}
                            />
                          ) : (
                            <AssistantMarkdown
                              key={`text-${seg.text.slice(0, 48)}`}
                              text={seg.text}
                              className={styles.excerpt}
                            />
                          ),
                        )
                      ) : (
                        <AssistantMarkdown text={c.context} className={styles.excerpt} />
                      ))}
                    {c.transaction_id && <span className={styles.rowTxnId}>txn: {c.transaction_id}</span>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </dialog>
  );
};

export default CitationPopover;
